import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2Icon, CheckIcon, FileCheckIcon, RefreshCwIcon } from 'lucide-react';
import { hmdaByReason, lenderNames } from '../../data/withdraw';
import { DeadForm } from '../../types/withdraw';
import { easeOut, inputClasses } from '../../utils/styles';
import { Expand } from '../ui/Expand';
import { FormField } from '../ui/FormField';
interface LenderSyncBannerProps {
  form: DeadForm;
  onChange: (patch: Partial<DeadForm>) => void;
}
const fade = {
  initial: {
    opacity: 0,
    y: 6
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -6
  },
  transition: {
    duration: 0.2,
    ease: easeOut
  }
};
export function LenderSyncBanner({
  form,
  onChange
}: LenderSyncBannerProps) {
  const hmda = form.reason ? hmdaByReason[form.reason] : null;
  const checked = form.withdrawnWithLender;
  return <div className="space-y-3" data-id="element-59" data-mp="1o8921fszcx0c">
      <p className="text-[13px] font-medium text-slate-700" data-id="element-60" data-mp="1k8qjursjn017">Lender reporting</p>

      <AnimatePresence mode="wait" initial={false} data-id="element-61" data-mp="1l9mjhm8h3wgj">
        {form.lenderVariant === 'sync' ? <motion.div key="sync" {...fade} className="flex flex-col gap-4 rounded-2xl border border-accent-100 bg-accent-50/70 p-4 sm:flex-row sm:items-center" data-id="element-62" data-mp="1q869pyu3nuep">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-accent-600 shadow-soft" data-id="element-63" data-mp="1g9qf3lvv630g">
              <RefreshCwIcon className="h-5 w-5" aria-hidden="true" data-id="element-64" data-mp="240vwi5hgd3cv" />
            </span>
            <div className="flex-1" data-id="element-65" data-mp="28z75yf3u5buk">
              <p className="text-sm font-semibold text-slate-900" data-id="element-66" data-mp="28y5s62vfqb95">HMDA will be reported from the lender sync</p>
              <p className="mt-0.5 text-sm text-slate-600" data-id="element-67" data-mp="1v5g3fqoius8c">
                Withdraw this file with {lenderNames.sync} to keep your MCR clean.
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 self-start whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-accent-200 sm:self-center" data-id="element-68" data-mp="2mx5kplm2jaal">
              <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-69" data-mp="31km96ffewmya">
                <span className="absolute inline-flex h-full w-full rounded-full bg-sun-400 opacity-60 motion-safe:animate-ping" data-id="element-70" data-mp="2xl543n3wdrtn" />
                <span className="relative h-2 w-2 rounded-full bg-sun-500" data-id="element-71" data-mp="2smtv1elfflch" />
              </span>
              Withdrawn with lender: <span className="text-sun-700" data-id="element-72" data-mp="2imjhfsvhcbws">Pending</span>
            </span>
          </motion.div> : <motion.div key="nonsync" {...fade} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4" data-id="element-73" data-mp="2dvb7mvq8moj3">
            <div className="flex gap-4" data-id="element-74" data-mp="29tga82bfgval">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-slate-600 shadow-soft" data-id="element-75" data-mp="29s543vuifbgb">
                <Building2Icon className="h-5 w-5" aria-hidden="true" data-id="element-76" data-mp="1zw21kovqxasu" />
              </span>
              <div data-id="element-77" data-mp="1zxwgnmkc68p0">
                <p className="text-sm font-semibold text-slate-900" data-id="element-78" data-mp="1uwgrpjf1fi1c">{lenderNames.nonsync} doesn't sync with Mortgo</p>
                <p className="mt-0.5 text-sm text-slate-600" data-id="element-79" data-mp="1kttoclyurfdg">
                  Withdraw the file directly with the lender, then confirm below so HMDA is reported correctly.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-white p-4 ring-1 ring-slate-200" data-id="element-80" data-mp="1urzlyrdmlzci">
              <label className="flex cursor-pointer items-start gap-3" data-id="element-81" data-mp="1zozud8xy7e6s">
                <input type="checkbox" className="peer sr-only" checked={checked} required onChange={e => onChange({
              withdrawnWithLender: e.target.checked,
              lenderWithdrawDate: e.target.checked ? form.lenderWithdrawDate || form.withdrawDate : form.lenderWithdrawDate
            })} data-id="element-82" data-mp="1232dffz7vgwy" />
                <span aria-hidden="true" className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border-2 transition-colors duration-150 peer-focus-visible:ring-4 peer-focus-visible:ring-primary-100 ${checked ? 'border-primary-700 bg-primary-700' : 'border-slate-300 bg-white'}`} data-id="element-83" data-mp="oaw001ng7o17">
                  {checked && <motion.span initial={{
                scale: 0.6,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.15
              }} data-id="element-84" data-mp="2uz4t7snftvzx">
                      <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-85" data-mp="38ozvk7rs1k4o" />
                    </motion.span>}
                </span>
                <span className="text-sm font-medium text-slate-800" data-id="element-86" data-mp="3ip8qlia18zih">
                  I've withdrawn this file with the lender <span className="text-coral-600" data-id="element-87" data-mp="3dr7bt9jn3mct">*</span>
                </span>
              </label>
              <Expand show={checked} data-id="element-88" data-mp="1obr47yld68p1">
                <div className="pb-1 pl-8 pt-4" data-id="element-89" data-mp="1ocspevw3gbkh">
                  <FormField label="Date withdrawn with lender" htmlFor="lender-withdraw-date" required data-id="element-90" data-mp="1jcwl0vjhii4e">
                    <input id="lender-withdraw-date" type="date" value={form.lenderWithdrawDate} onChange={e => onChange({
                  lenderWithdrawDate: e.target.value
                })} className={`${inputClasses} sm:max-w-[240px]`} data-id="element-91" data-mp="3v0jbgv7vukwi" />
                  </FormField>
                </div>
              </Expand>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500" data-id="element-92" data-mp="3catj0erleb7r">
              <span data-id="element-93" data-mp="3h5qnr9hu9yeu">Reporting preview</span>
              <AnimatePresence mode="wait" initial={false} data-id="element-94" data-mp="32b2jtzs3781c">
                <motion.span key={hmda ? `${hmda.code}-${hmda.label}` : 'none'} initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.16,
              ease: easeOut
            }} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold ${hmda ? 'bg-white text-slate-800 ring-1 ring-slate-200' : 'border border-dashed border-slate-300 text-slate-500'}`} data-id="element-95" data-mp="32cndzoahooyo">
                  <FileCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-96" data-mp="u4qzxkfnn5us" />
                  {hmda ? `HMDA: ${hmda.code} – ${hmda.label}` : 'HMDA: pick a reason to preview'}
                </motion.span>
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}